if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopLayoutMql = window.matchMedia('(min-width: 990px)');
        this.setupMainMediaGestures();
        this.setupMainMediaDesktopNavigation();
        this.setupVerticalStackedLayout();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (this.queueVerticalStackedLayoutUpdate) {
          window.removeEventListener('resize', this.queueVerticalStackedLayoutUpdate);
          window.removeEventListener('scroll', this.queueVerticalStackedScrollUpdate);
          if (typeof this.desktopLayoutMql?.removeEventListener === 'function') {
            this.desktopLayoutMql.removeEventListener('change', this.queueVerticalStackedLayoutUpdate);
          } else if (typeof this.desktopLayoutMql?.removeListener === 'function') {
            this.desktopLayoutMql.removeListener(this.queueVerticalStackedLayoutUpdate);
          }
        }

        if (this.verticalStackedResizeFrame) {
          window.cancelAnimationFrame(this.verticalStackedResizeFrame);
        }

        if (this.verticalStackedScrollFrame) {
          window.cancelAnimationFrame(this.verticalStackedScrollFrame);
        }

        this.verticalStackedResizeObserver?.disconnect();
      }

      setupVerticalStackedLayout() {
        if (this.dataset.desktopLayout !== 'vertical_stacked') return;

        this.verticalStackedProduct = this.closest('.product--vertical_stacked');
        if (!this.verticalStackedProduct) return;

        this.verticalStackedMediaWrapper = this.verticalStackedProduct.querySelector('.product__media-wrapper');
        this.verticalStackedInfo = this.verticalStackedProduct.querySelector('.product__info-container');
        this.verticalStackedResizeFrame = null;
        this.verticalStackedScrollFrame = null;
        this.verticalStackedGalleryScrollDistance = 0;
        this.updateVerticalStackedLayout = this.updateVerticalStackedLayout.bind(this);
        this.queueVerticalStackedLayoutUpdate = this.queueVerticalStackedLayoutUpdate.bind(this);
        this.updateVerticalStackedScroll = this.updateVerticalStackedScroll.bind(this);
        this.queueVerticalStackedScrollUpdate = this.queueVerticalStackedScrollUpdate.bind(this);

        this.queueVerticalStackedLayoutUpdate();
        window.addEventListener('resize', this.queueVerticalStackedLayoutUpdate, { passive: true });
        window.addEventListener('scroll', this.queueVerticalStackedScrollUpdate, { passive: true });
        if (typeof this.desktopLayoutMql.addEventListener === 'function') {
          this.desktopLayoutMql.addEventListener('change', this.queueVerticalStackedLayoutUpdate);
        } else if (typeof this.desktopLayoutMql.addListener === 'function') {
          this.desktopLayoutMql.addListener(this.queueVerticalStackedLayoutUpdate);
        }

        if ('ResizeObserver' in window) {
          this.verticalStackedResizeObserver = new ResizeObserver(this.queueVerticalStackedLayoutUpdate);
          this.verticalStackedResizeObserver.observe(this);
          if (this.verticalStackedInfo) this.verticalStackedResizeObserver.observe(this.verticalStackedInfo);
        }

        this.querySelectorAll('img').forEach((image) => {
          if (image.complete) return;
          image.addEventListener('load', this.queueVerticalStackedLayoutUpdate, { once: true });
        });
      }

      queueVerticalStackedLayoutUpdate() {
        if (this.verticalStackedResizeFrame) {
          window.cancelAnimationFrame(this.verticalStackedResizeFrame);
        }

        this.verticalStackedResizeFrame = window.requestAnimationFrame(this.updateVerticalStackedLayout);
      }

      queueVerticalStackedScrollUpdate() {
        if (this.verticalStackedScrollFrame) return;

        this.verticalStackedScrollFrame = window.requestAnimationFrame(this.updateVerticalStackedScroll);
      }

      updateVerticalStackedLayout() {
        this.verticalStackedResizeFrame = null;
        if (!this.verticalStackedProduct) return;

        if (!this.desktopLayoutMql.matches) {
          this.clearVerticalStackedLayout();
          return;
        }

        const stickyOffset = parseFloat(getComputedStyle(this.verticalStackedMediaWrapper || this).top) || 0;
        const viewportHeight = Math.max(window.innerHeight - stickyOffset, 0);
        const galleryHeight = Math.ceil(this.scrollHeight || this.getBoundingClientRect().height);
        const infoHeight = Math.ceil(this.verticalStackedInfo?.scrollHeight || this.verticalStackedInfo?.getBoundingClientRect().height || 0);

        if (!galleryHeight || !infoHeight || !viewportHeight) {
          this.clearVerticalStackedLayout();
          return;
        }

        this.verticalStackedGalleryScrollDistance = Math.max(galleryHeight - viewportHeight, 0);
        const sectionHeight = this.verticalStackedGalleryScrollDistance + Math.max(infoHeight, viewportHeight);
        const infoBoundaryHeight = this.verticalStackedGalleryScrollDistance + infoHeight;

        this.verticalStackedProduct.style.setProperty(
          '--product-vertical-stacked-section-height',
          `${Math.ceil(sectionHeight)}px`
        );
        this.verticalStackedProduct.style.setProperty(
          '--product-vertical-stacked-info-boundary-height',
          `${Math.ceil(infoBoundaryHeight)}px`
        );
        this.verticalStackedProduct.style.setProperty(
          '--product-vertical-stacked-viewport-height',
          `${Math.ceil(viewportHeight)}px`
        );

        this.queueVerticalStackedScrollUpdate();
      }

      updateVerticalStackedScroll() {
        this.verticalStackedScrollFrame = null;
        if (!this.verticalStackedProduct || !this.desktopLayoutMql.matches) return;

        const stickyOffset = parseFloat(getComputedStyle(this.verticalStackedMediaWrapper || this).top) || 0;
        const productTop = this.verticalStackedProduct.getBoundingClientRect().top + window.scrollY;
        const scrollProgress = window.scrollY - productTop + stickyOffset;
        const clampedProgress = Math.min(Math.max(scrollProgress, 0), this.verticalStackedGalleryScrollDistance || 0);

        this.verticalStackedProduct.style.setProperty(
          '--product-vertical-stacked-gallery-shift',
          `${Math.round(clampedProgress * -1)}px`
        );
      }

      clearVerticalStackedLayout() {
        this.verticalStackedGalleryScrollDistance = 0;
        this.verticalStackedProduct.style.removeProperty('--product-vertical-stacked-section-height');
        this.verticalStackedProduct.style.removeProperty('--product-vertical-stacked-info-boundary-height');
        this.verticalStackedProduct.style.removeProperty('--product-vertical-stacked-viewport-height');
        this.verticalStackedProduct.style.removeProperty('--product-vertical-stacked-gallery-shift');
      }

      setupMainMediaGestures() {
        this.mediaDragConfig = {
          axisLockThreshold: 12,
          swipeThreshold: 60,
          tapTolerance: 28,
          clickSuppressDuration: 250,
          transitionDuration: 320,
        };
        this.mediaDragState = null;
        this.suppressMediaClickUntil = 0;
        this.isGestureNavigation = false;
        this.gestureTransitionTimeout = null;

        this.viewerSlider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!this.viewerSlider || !window.PointerEvent) return;

        this.viewerSlider.style.touchAction = 'pan-y';
        this.viewerSlider.addEventListener('pointerdown', this.onMainMediaPointerDown.bind(this));
        this.viewerSlider.addEventListener('pointermove', this.onMainMediaPointerMove.bind(this), { passive: false });
        this.viewerSlider.addEventListener('pointerup', this.onMainMediaPointerUp.bind(this));
        this.viewerSlider.addEventListener('pointercancel', this.onMainMediaPointerCancel.bind(this));
        this.viewerSlider.addEventListener('click', this.onMainMediaClickCapture.bind(this), true);
      }

      openLightboxFromToggle(toggleButton) {
        if (!toggleButton) return;

        const modalOpener = toggleButton.closest('modal-opener');
        if (!modalOpener) return;

        const modal = document.querySelector(modalOpener.getAttribute('data-modal'));
        if (modal?.show) {
          modal.show(toggleButton);
          return;
        }

        toggleButton.click();
      }

      setupMainMediaDesktopNavigation() {
        if (!this.elements.viewer) return;

        this.elements.viewer.querySelectorAll('[data-main-media-nav]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.navigateMainMediaBy(button.dataset.mainMediaNav === 'next' ? 1 : -1);
          });
        });
      }

      getMainMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]') || []);
      }

      onMainMediaPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.getMainMediaItems().length < 2) return;

        const mediaItem = event.target.closest('.product__media-item[data-media-id]');
        if (!mediaItem || !this.viewerSlider.contains(mediaItem)) return;

        this.mediaDragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          latestX: event.clientX,
          latestY: event.clientY,
          modalToggle:
            event.target.closest('.product__modal-opener--image .product__media-toggle') ||
            event.target.closest('.product__modal-opener--image')?.querySelector('.product__media-toggle'),
          axisLocked: false,
          isHorizontal: false,
          hasDragged: false,
        };

        if (typeof this.viewerSlider.setPointerCapture === 'function') {
          this.viewerSlider.setPointerCapture(event.pointerId);
        }
      }

      onMainMediaPointerMove(event) {
        if (!this.mediaDragState || event.pointerId !== this.mediaDragState.pointerId) return;

        this.mediaDragState.latestX = event.clientX;
        this.mediaDragState.latestY = event.clientY;

        const deltaX = this.mediaDragState.latestX - this.mediaDragState.startX;
        const deltaY = this.mediaDragState.latestY - this.mediaDragState.startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (!this.mediaDragState.axisLocked) {
          if (absX < this.mediaDragConfig.axisLockThreshold && absY < this.mediaDragConfig.axisLockThreshold) return;
          this.mediaDragState.axisLocked = true;
          this.mediaDragState.isHorizontal = absX > absY * 1.2;
        }

        if (!this.mediaDragState.isHorizontal) return;

        this.mediaDragState.hasDragged = true;
        event.preventDefault();
      }

      onMainMediaPointerUp(event) {
        if (!this.mediaDragState || event.pointerId !== this.mediaDragState.pointerId) return;

        const state = this.mediaDragState;
        this.mediaDragState = null;
        this.releaseMainMediaPointerCapture(state.pointerId);

        const deltaX = state.latestX - state.startX;
        const deltaY = state.latestY - state.startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (!state.hasDragged) {
          if (absX <= this.mediaDragConfig.tapTolerance && absY <= this.mediaDragConfig.tapTolerance) {
            this.openLightboxFromToggle(state.modalToggle);
          }
          return;
        }

        if (!state.isHorizontal) return;

        if (absX < this.mediaDragConfig.swipeThreshold || absX <= absY) {
          if (absX <= this.mediaDragConfig.tapTolerance && absY <= this.mediaDragConfig.tapTolerance) {
            this.openLightboxFromToggle(state.modalToggle);
          }
          return;
        }

        this.navigateMainMediaBy(deltaX < 0 ? 1 : -1);
        this.suppressMediaClickUntil = Date.now() + this.mediaDragConfig.clickSuppressDuration;
        event.preventDefault();
      }

      onMainMediaPointerCancel(event) {
        if (!this.mediaDragState || event.pointerId !== this.mediaDragState.pointerId) return;
        this.releaseMainMediaPointerCapture(this.mediaDragState.pointerId);
        this.mediaDragState = null;
      }

      releaseMainMediaPointerCapture(pointerId) {
        if (
          typeof this.viewerSlider?.releasePointerCapture === 'function' &&
          typeof this.viewerSlider?.hasPointerCapture === 'function' &&
          this.viewerSlider.hasPointerCapture(pointerId)
        ) {
          this.viewerSlider.releasePointerCapture(pointerId);
        }
      }

      onMainMediaClickCapture(event) {
        if (Date.now() > this.suppressMediaClickUntil) return;
        if (!event.target.closest('.product__modal-opener button, .deferred-media__poster')) return;

        event.preventDefault();
        event.stopPropagation();
      }

      navigateMainMediaBy(step) {
        const mediaItems = this.getMainMediaItems();
        if (mediaItems.length < 2) return;

        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const currentIndex = activeIndex >= 0 ? activeIndex : 0;
        let targetIndex = currentIndex + step;
        if (targetIndex > mediaItems.length - 1) targetIndex = 0;
        if (targetIndex < 0) targetIndex = mediaItems.length - 1;
        if (targetIndex === currentIndex) return;

        this.isGestureNavigation = true;
        this.classList.remove('media-gallery--gesture-next', 'media-gallery--gesture-prev');
        this.classList.add(step > 0 ? 'media-gallery--gesture-next' : 'media-gallery--gesture-prev');
        if (this.gestureTransitionTimeout) {
          clearTimeout(this.gestureTransitionTimeout);
        }
        this.gestureTransitionTimeout = window.setTimeout(() => {
          this.isGestureNavigation = false;
          this.classList.remove('media-gallery--gesture-next', 'media-gallery--gesture-prev');
        }, this.mediaDragConfig.transitionDuration);

        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const currentMediaId = event?.detail?.currentElement?.dataset?.mediaId;
        if (!currentMediaId || !this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentMediaId}"]`);
        if (!thumbnail) return;

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: this.isGestureNavigation ? 'smooth' : 'auto',
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!activeThumbnail) return;
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
